import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { IMG } from "../lib/api";
import Logo from "./Logo";

const EASE = [0.22, 1, 0.36, 1];
const T_FACE = 2000; // eyes -> face
const T_FOREST = 3800; // face -> forest
const T_BLINK = 5600; // forest -> blink
const T_BLACK = 6700; // blink -> fade to black
const T_REVEAL = 7700; // black -> site emerges
const T_DONE = 8900; // hand off to the website

export default function IntroCinematic({ onDone }) {
  const [phase, setPhase] = useState("eyes");
  const timers = useRef([]);

  useEffect(() => {
    const t = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    t(() => setPhase("face"), T_FACE);
    t(() => setPhase("forest"), T_FOREST);
    t(() => setPhase("blink"), T_BLINK);
    t(() => setPhase("black"), T_BLACK);
    t(() => setPhase("reveal"), T_REVEAL);
    t(() => {
      try {
        sessionStorage.setItem("rcscw-intro", "1");
      } catch (e) {}
      onDone();
    }, T_DONE);
    return () => timers.current.forEach(clearTimeout);
  }, [onDone]);

  const skip = () => {
    timers.current.forEach(clearTimeout);
    try {
      sessionStorage.setItem("rcscw-intro", "1");
    } catch (e) {}
    onDone();
  };

  const sceneGone = phase === "black" || phase === "reveal";
  const blink = phase === "blink";
  const reveal = phase === "reveal";

  // One leopard, one picture — the eyes never leave you.
  // A single continuous pull-back runs from the start until the blink.

  return (
    <motion.div
      className="fixed inset-0 z-[100] overflow-hidden bg-black"
      data-testid="intro-overlay"
      animate={{ opacity: reveal ? 0 : 1 }}
      transition={{ duration: 1.15, ease: "easeInOut" }}
    >
      {/* The scene — fades to pure black after the blink */}
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: sceneGone ? 0 : 1, scale: phase === "black" ? 1.04 : 1 }}
        transition={{
          opacity: { duration: 0.95, ease: "easeInOut" },
          scale: { duration: 1.1, ease: "easeInOut" },
        }}
      >
        {/* One leopard, one continuous shot — the stare locks in, the camera pulls back at a constant speed, then the blink. No cuts, no picture swaps */}
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.75, x: 0, y: 0 }}
          animate={{
            scale: sceneGone ? 1.34 : [1.75, 1.76, 1.24],
            x: sceneGone ? 0 : [0, -7, 0],
            y: sceneGone ? 0 : [0, 5, 0],
            filter: blink ? ["brightness(1)", "brightness(0.5)", "brightness(1.06)"] : "brightness(1)",
          }}
          transition={{
            scale: sceneGone ? { duration: 1.2, ease: "easeInOut" } : { duration: T_BLINK / 1000, times: [0, 0.2, 1], ease: "linear" },
            x: { duration: T_BLINK / 1000, times: [0, 0.5, 1], ease: "easeInOut" },
            y: { duration: T_BLINK / 1000, times: [0, 0.5, 1], ease: "easeInOut" },
            filter: blink ? { duration: 1.05, times: [0, 0.34, 1], ease: "easeInOut" } : { duration: 0.3 },
          }}
        >
          <img src={IMG.leopardEyes} alt="Extreme close-up of a snow leopard's eyes looking directly at you" className="h-full w-full object-cover object-[22%_50%] sm:object-center" />
        </motion.div>

        {/* Atmosphere */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink/70 via-transparent to-ink" />
        <div className="pointer-events-none absolute inset-0 opacity-20 mix-blend-screen" style={{ animation: "drift 10s ease-in-out infinite alternate" }}>
          <img src={IMG.celestialSky} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="pointer-events-none absolute inset-0">
          {[...Array(8)].map((_, i) => (
            <span
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${14 + i * 10}%`,
                bottom: `${10 + (i % 4) * 6}%`,
                width: 3 + (i % 3),
                height: 3 + (i % 3),
                background: "radial-gradient(circle, rgba(226,232,240,0.95), rgba(226,232,240,0))",
                animation: `ember-rise ${2.8 + (i % 5) * 0.6}s ease-out ${i * 0.6}s infinite`,
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* The blink — curved lids, like the leopard closing its own eyes */}
      <motion.div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 h-[54%] origin-top"
        style={{
          background: "linear-gradient(to bottom, #02060f 0%, #060d20 82%, rgba(6,13,32,0))",
          borderRadius: "0 0 50% 50% / 0 0 90px 90px",
        }}
        initial={{ scaleY: 0 }}
        animate={{ scaleY: blink ? [0, 1, 1, 0] : 0 }}
        transition={blink ? { duration: 1.05, times: [0, 0.32, 0.52, 1], ease: "easeInOut" } : { duration: 0.3 }}
      />
      <motion.div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-[54%] origin-bottom"
        style={{
          background: "linear-gradient(to top, #02060f 0%, #060d20 82%, rgba(6,13,32,0))",
          borderRadius: "50% 50% 0 0 / 90px 90px 0 0",
        }}
        initial={{ scaleY: 0 }}
        animate={{ scaleY: blink ? [0, 1, 1, 0] : 0 }}
        transition={blink ? { duration: 1.05, times: [0, 0.32, 0.52, 1], ease: "easeInOut" } : { duration: 0.3 }}
      />

      {/* Logo */}
      <motion.div
        className="absolute left-6 top-6 z-30 sm:left-10 sm:top-10"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: sceneGone ? 0 : 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
      >
        <Logo size={44} />
      </motion.div>

      {/* Titles — fade into the black */}
      <motion.div
        className="absolute inset-x-0 bottom-0 z-30 px-6 pb-14 sm:px-10 sm:pb-16"
        animate={{ opacity: sceneGone ? 0 : 1 }}
        transition={{ duration: 0.7, ease: "easeInOut" }}
      >
        <div className="overflow-hidden">
          <motion.p className="eyebrow" initial={{ y: "130%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease: EASE }}>
            Rotaract Club of Sophia College for Women
          </motion.p>
        </div>
        <div className="mt-3 overflow-hidden">
          <motion.h1
            className="metallic font-display text-[17vw] font-semibold leading-[0.9] sm:text-[13vw] lg:text-[9.5rem]"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.15, delay: 1.5, ease: EASE }}
          >
            FORGED
          </motion.h1>
        </div>
        <div className="mt-2 overflow-hidden">
          <motion.p
            className="font-display text-2xl italic text-silver-dim sm:text-3xl"
            initial={{ y: "130%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay: 2.7, ease: EASE }}
          >
            — Reclaiming the Fire —
          </motion.p>
        </div>
        <motion.div
          className="mt-8 h-px w-full max-w-md origin-left bg-gradient-to-r from-ember via-silver-dim to-transparent"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 0.999 }}
          transition={{ duration: T_BLINK / 1000, delay: 0.3, ease: "easeInOut" }}
        />
      </motion.div>

      {/* Skip — visible throughout */}
      <motion.button
        onClick={skip}
        className="absolute bottom-6 right-6 z-40 border border-silver/40 bg-ink/50 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-silver backdrop-blur transition-colors hover:border-silver hover:bg-ink/80 sm:bottom-8 sm:right-8"
        data-testid="skip-intro-btn"
        initial={{ opacity: 0 }}
        animate={{ opacity: reveal ? 0 : 1 }}
        transition={{ delay: 0.6, duration: 0.6 }}
      >
        Skip Intro
      </motion.button>
    </motion.div>
  );
}
